import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, MessageSquare, ArrowRight, Quote } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Embeddable testimonials section.
 * Shows up to 6 best approved reviews + a CTA button to /anmeldelser.
 *
 * Props:
 *  - title: optional override (default: "Hva kundene våre sier")
 *  - subtitle: optional subtitle
 *  - variant: "dark" | "light" (default: "dark")
 */
export default function TestimonialsSection({
  title = "Hva kundene våre sier",
  subtitle = "Ekte tilbakemeldinger fra fornøyde kunder",
  variant = "dark",
}) {
  const [data, setData] = useState({ items: [], stats: { count: 0, average_rating: 0 } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/testimonials/public?limit=6`)
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data.items?.length) return null;

  const isDark = variant === "dark";
  const bgClass = isDark ? "bg-[#050505]" : "bg-white";
  const cardClass = isDark
    ? "bg-[#0A0A0A] border-white/10"
    : "bg-white border-gray-200 shadow-sm";
  const titleClass = isDark ? "text-white" : "text-gray-900";
  const subtitleClass = isDark ? "text-[#94A3B8]" : "text-gray-600";
  const textClass = isDark ? "text-[#CBD5E1]" : "text-gray-700";
  const nameClass = isDark ? "text-white" : "text-gray-900";

  return (
    <section
      className={`${bgClass} py-16 md:py-24 px-4`}
      data-testid="testimonials-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-full mb-4">
            <MessageSquare className="w-4 h-4 text-[#F7931A]" />
            <span className="text-[#F7931A] font-semibold text-sm uppercase tracking-wider">
              Anmeldelser
            </span>
          </div>
          <h2
            className={`text-4xl sm:text-5xl lg:text-6xl font-['Unbounded'] font-bold ${titleClass} mb-4`}
          >
            {title}
          </h2>
          <p className={`${subtitleClass} text-base md:text-lg max-w-2xl mx-auto`}>
            {subtitle}
          </p>

          {/* Aggregate stats */}
          {data.stats.count > 0 && (
            <div className="inline-flex items-center gap-3 mt-6 px-5 py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-full">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-5 h-5 ${
                      n <= Math.round(data.stats.average_rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : isDark
                        ? "text-[#475569]"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className={`font-bold ${titleClass}`}>
                {data.stats.average_rating} / 5
              </span>
              <span className={subtitleClass}>
                ({data.stats.count}{" "}
                {data.stats.count === 1 ? "anmeldelse" : "anmeldelser"})
              </span>
            </div>
          )}
        </div>

        {/* Reviews grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.items.map((t) => (
            <Card
              key={t.testimonial_id}
              className={`${cardClass} hover:border-[#F7931A]/50 transition-colors`}
              data-testid={`testimonial-card-${t.testimonial_id}`}
            >
              <CardContent className="p-6">
                <Quote className="w-8 h-8 text-[#F7931A]/40 mb-3" />
                <div className="flex items-center gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`w-4 h-4 ${
                        n <= t.rating
                          ? "fill-yellow-400 text-yellow-400"
                          : isDark
                          ? "text-[#475569]"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
                <p className={`${textClass} mb-4 leading-relaxed line-clamp-5 min-h-[1.5rem]`}>
                  {t.comment ? `"${t.comment}"` : <span className="opacity-40">⭐⭐⭐⭐⭐</span>}
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F7931A] to-[#FFCD00] flex items-center justify-center text-white font-bold flex-shrink-0">
                    {t.name[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className={`${nameClass} font-semibold text-sm`}>{t.name}</p>
                    <p className={`${subtitleClass} text-xs`}>
                      {new Date(t.created_at).toLocaleDateString("no-NO", {
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* See all CTA */}
        {data.stats.count > 6 && (
          <div className="text-center mt-12">
            <Link to="/anmeldelser">
              <Button
                size="lg"
                className="bg-[#F7931A] text-white hover:bg-[#F7931A]/80 px-8"
                data-testid="see-all-testimonials-btn"
              >
                Se alle {data.stats.count} anmeldelser
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
